import { absoluteTime } from '@lib/time';
import { Button, KeyValue, Modal } from '@shared/ui';
import { useEffect, useState } from 'react';

import { DETAIL_FIELDS, DETAIL_LABELS, toDetail } from '../model/detail';
import { reasonLabel } from '../model/reasons';

import type { RefusalRecord } from '../schemas/refusals';

const COPIED_FOR_MS = 2_000;

/** Exactly the row's fields, and a way to copy the trace id into a support ticket. */
export function RefusalDetail({
    record,
    onClose,
}: {
    record: RefusalRecord | null;
    onClose: () => void;
}) {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), COPIED_FOR_MS);
        return () => clearTimeout(timer);
    }, [copied]);

    const detail = record ? toDetail(record) : null;
    const traceId = record?.traceId;

    return (
        <Modal
            open={record !== null}
            onClose={onClose}
            title="Refusal"
            footer={
                <>
                    {traceId !== undefined && (
                        <Button
                            variant="ghost"
                            onClick={() => {
                                void navigator.clipboard
                                    .writeText(traceId)
                                    .then(() => setCopied(true));
                            }}
                        >
                            {copied ? 'Copied' : 'Copy traceId'}
                        </Button>
                    )}
                    <Button onClick={onClose}>Close</Button>
                </>
            }
        >
            {detail && (
                <div data-testid="unidentified-refusals-detail">
                    {DETAIL_FIELDS.map((field) => (
                        <KeyValue key={field} label={DETAIL_LABELS[field]}>
                            {field === 'occurredAt'
                                ? absoluteTime(detail.occurredAt)
                                : field === 'reason'
                                  ? reasonLabel(detail.reason)
                                  : detail[field]}
                        </KeyValue>
                    ))}
                </div>
            )}
        </Modal>
    );
}
